import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

// Por tela (sin fechas; la temporada la marca la última compra; la colección solo filtra).
// Los artículos relacionados entre sí cuentan como una sola tela (idGrupo del back).
//   Recibidas              = lo que llegó en la última compra de la tela
//   Stock otras temporadas = lo que sigue en inventario de las demás compras
//   Venta                  = todo lo vendido de la tela
//   Stock                  = todo el inventario de la tela
const ID_LUCKY = "6633d63143ef7d22b847995d";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);


function ReporteEntradasSalidas() {
  const { user } = useContext(AuthContext);
  const URL_INVENTARIOS = `${process.env.REACT_APP_URL_INVENTARIOS}`;

  const [datos, setDatos] = useState(null);
  const [duenio, setDuenio] = useState("todos");
  const [search, setSearch] = useState("");
  const [coleccion, setColeccion] = useState("");
  const [soloStock, setSoloStock] = useState(true);
  const [sorting, setSorting] = useState({ field: "nombre", order: "asc" });
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  useEffect(() => {
    showLoader();
    axios
      .get(`${URL_INVENTARIOS}EntradasSalidasCompras`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setDatos(response.data);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  }, []);

  const clientes = useMemo(() => {
    const m = {};
    (datos || []).forEach((r) => {
      if (r.idCliente !== ID_LUCKY) m[r.idCliente] = r.cliente;
    });
    return Object.entries(m).sort((a, b) => (a[1] > b[1] ? 1 : -1));
  }, [datos]);

  // Una fila por tela (o grupo de artículos relacionados) con sus compras
  const grupos = useMemo(() => {
    if (!datos) return [];
    const incluir = (r) =>
      duenio === "todos" ||
      (duenio === "lucky" && r.idCliente === ID_LUCKY) ||
      (duenio === "programas" && r.idCliente !== ID_LUCKY) ||
      r.idCliente === duenio;

    const porGrupo = {};
    datos.filter(incluir).forEach((r) => {
      const idGrupo = String(r.idGrupo || r.idArticulo);
      const t = (porGrupo[idGrupo] = porGrupo[idGrupo] || {
        idGrupo,
        articulos: {},
        colecciones: {},
        compras: {},
      });
      t.articulos[r.idArticulo] = { codigo: r.codigo, nombre: r.nombre || "" };
      if (r.idColeccion) t.colecciones[r.idColeccion] = r.coleccion;
      const c = (t.compras[r.idCompra] = t.compras[r.idCompra] || {
        fecha: r.fechaCompra || "",
        folio: r.folioCompra || 0,
        recibido: 0,
        vendido: 0,
        stock: 0,
      });
      c.recibido += r.recibido;
      c.vendido += r.vendido;
      c.stock += r.stock;
    });

    return Object.values(porGrupo).map((t) => {
      const compras = Object.values(t.compras);
      const ultima = compras.reduce((u, c) =>
        !u || c.fecha > u.fecha || (c.fecha === u.fecha && c.folio > u.folio) ? c : u
      , null);
      const vendido = compras.reduce((s, c) => s + c.vendido, 0);
      const stock = compras.reduce((s, c) => s + c.stock, 0);
      const arts = Object.values(t.articulos).sort((a, b) => (a.codigo || 0) - (b.codigo || 0));
      return {
        idGrupo: t.idGrupo,
        codigo: arts.map((a) => String(a.codigo ?? "")).join(" / "),
        nombre: [...new Set(arts.map((a) => a.nombre))].join(" / "),
        colecciones: t.colecciones,
        inicial: stock - (ultima ? ultima.stock : 0),
        recibido: ultima ? ultima.recibido : 0,
        vendido,
        final: stock,
      };
    });
  }, [datos, duenio]);

  // Con "solo con stock" solo se ofrecen las colecciones que tienen stock
  const colecciones = useMemo(() => {
    const m = {};
    const conStock = {};
    grupos.forEach((g) => {
      Object.entries(g.colecciones).forEach(([id, nombre]) => {
        m[id] = nombre;
        if (g.final > 0.01) conStock[id] = true;
      });
    });
    return Object.entries(m)
      .filter(([id]) => !soloStock || conStock[id] || id === coleccion)
      .sort((a, b) => (a[1] > b[1] ? 1 : -1));
  }, [grupos, soloStock, coleccion]);

  const filas = useMemo(() => {
    // Un grupo entra a la colección si alguno de sus artículos es de ella
    let lista = grupos.filter((g) => !coleccion || g.colecciones[coleccion]);

    if (soloStock) lista = lista.filter((f) => f.final > 0.01);

    if (search) {
      const s = search.toLowerCase();
      lista = lista.filter(
        (f) => f.nombre.toLowerCase().includes(s) || f.codigo.toLowerCase().includes(s)
      );
    }

    const dir = sorting.order === "desc" ? -1 : 1;
    if (["nombre", "codigo"].includes(sorting.field)) {
      lista.sort((a, b) =>
        dir * a[sorting.field].localeCompare(b[sorting.field], undefined, { numeric: true })
      );
    } else if (sorting.field) {
      lista.sort((a, b) => dir * (a[sorting.field] - b[sorting.field]));
    }
    return lista;
  }, [grupos, coleccion, soloStock, search, sorting]);

  const totales = filas.reduce(
    (t, f) => ({
      inicial: t.inicial + f.inicial,
      recibido: t.recibido + f.recibido,
      vendido: t.vendido + f.vendido,
      final: t.final + f.final,
    }),
    { inicial: 0, recibido: 0, vendido: 0, final: 0 }
  );

  const nombreDuenio = () => {
    if (duenio === "todos") return "Todos";
    if (duenio === "lucky") return "LUCKY TEXTIL";
    if (duenio === "programas") return "Programas de clientes";
    const c = clientes.find((x) => x[0] === duenio);
    return c ? c[1] : "";
  };

  const nombreColeccion = () => {
    const c = colecciones.find((x) => x[0] === coleccion);
    return c ? ` - Colección ${c[1]}` : "";
  };

  const titulos = ["Codigo", "Tela", "Stock otras temporadas", "Recibidas", "Venta", "Stock"];

  const headers = [
    { name: titulos[0], field: "codigo", sortable: true },
    { name: titulos[1], field: "nombre", sortable: true },
    { name: titulos[2], field: "inicial", sortable: true, align: "center" },
    { name: titulos[3], field: "recibido", sortable: true, align: "center" },
    { name: titulos[4], field: "vendido", sortable: true, align: "center" },
    { name: titulos[5], field: "final", sortable: true, align: "center" },
  ];

  function PDFTabla() {
    const doc = new jsPDF("landscape");
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 10, 45, 15);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 120, 10, 40, 16);
    doc.text(`Entradas y Salidas - ${nombreDuenio()}${nombreColeccion()}`, 15, 35);
    doc.autoTable({
      head: [titulos],
      body: filas.map((f) => [
        f.codigo,
        f.nombre,
        fmt(f.inicial),
        fmt(f.recibido),
        fmt(f.vendido),
        fmt(f.final),
      ]),
      foot: [
        [
          "Total",
          "",
          fmt(totales.inicial),
          fmt(totales.recibido),
          fmt(totales.vendido),
          fmt(totales.final),
        ],
      ],
      showFoot: "lastPage",
      startY: 40,
      columnStyles: {
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section === "head" && data.column.index >= 2) {
          data.cell.styles.halign = "center";
        }
      },
    });
    doc.save(`EntradasSalidas.pdf`);
  }

  function excel() {
    const option = {
      fileName: "EntradasSalidas",
      datas: [
        {
          sheetData: filas.map((f) => ({
            Codigo: f.codigo,
            Tela: f.nombre,
            OtrasTemporadas: +f.inicial.toFixed(2),
            Recibidas: +f.recibido.toFixed(2),
            Venta: +f.vendido.toFixed(2),
            Stock: +f.final.toFixed(2),
          })),
          sheetName: "EntradasSalidas",
          sheetFilter: [
            "Codigo",
            "Tela",
            "OtrasTemporadas",
            "Recibidas",
            "Venta",
            "Stock",
          ],
          sheetHeader: titulos,
        },
      ],
    };
    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.inventarios ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuInventarios"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Entradas y Salidas</h3>
          <Row>
            <Col md={3}>
              <Label className="mr-sm-2">Inventario de</Label>
              <Input
                bsSize="sm"
                type="select"
                value={duenio}
                onChange={(e) => setDuenio(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="lucky">LUCKY TEXTIL</option>
                <option value="programas">Programas de clientes (todos)</option>
                {clientes.map(([id, nombre]) => (
                  <option key={id} value={id}>
                    {nombre}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Colección</Label>
              <Input
                bsSize="sm"
                type="select"
                value={coleccion}
                onChange={(e) => setColeccion(e.target.value)}
              >
                <option value="">Todas</option>
                {colecciones.map(([id, nombre]) => (
                  <option key={id} value={id}>
                    {nombre}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Buscar tela</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="Nombre o código"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <br />
              <Label check style={{ marginLeft: "20px" }}>
                <Input
                  type="checkbox"
                  checked={soloStock}
                  onChange={(e) => setSoloStock(e.target.checked)}
                />{" "}
                Solo telas con stock
              </Label>
            </Col>
          </Row>
          <br />
          <Table size="sm" striped borderless className="table-responsive-xl">
              <TableHeader
                headers={headers}
                onSorting={(field, order) => setSorting({ field, order })}
              />
              <tbody>
                {filas.map((f) => (
                  <tr key={f.idGrupo}>
                    <td>{f.codigo}</td>
                    <td>{f.nombre}</td>
                    <td align="center">{fmt(f.inicial)}</td>
                    <td align="center">{fmt(f.recibido)}</td>
                    <td align="center">{fmt(f.vendido)}</td>
                    <td align="center">{fmt(f.final)}</td>
                  </tr>
                ))}
                <tr>
                  <td></td>
                  <td className="negrita">Total</td>
                  <td className="negrita" align="center">{fmt(totales.inicial)}</td>
                  <td className="negrita" align="center">{fmt(totales.recibido)}</td>
                  <td className="negrita" align="center">{fmt(totales.vendido)}</td>
                  <td className="negrita" align="center">{fmt(totales.final)}</td>
                </tr>
              </tbody>
            </Table>
        </div>
      ) : undefined}

      {loader}
    </>
  );
}

export default ReporteEntradasSalidas;
